import {
  Button,
  Divider,
  Input,
  Modal,
  Radio,
  Typography,
} from "antd";
import React, {
  Dispatch,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from "react";
import Flex from "../../../components/Flex";
import {
  getPaymentToken,
  completedLocalTransaction,
} from "../../../store/actions/orderActionCreators";
import { Skeleton } from "../../../components/Skeleton";
import { SelectedOrder } from "../OrderPage.types";
import { useIsDarkModeEnabled } from "../../../hooks/useIsDarkModeEnabled";
import {
  Action,
  ActionType,
  PaymentModalProps,
  ResponseCode,
  TransactionResponse,
} from "./PaymentModal-types";

const totalDueLabel = "Total Due";

const parseResponse = (str: string): ActionType => {
  const values: ActionType = {
    action: null,
    height: undefined,
    width: undefined,
  };
  const params = str.split("&");
  params.forEach((str) => {
    const pair: string[] = str.split("=");
    if (
      pair[1] === Action.RESIZE_WINDOW ||
      pair[1] === Action.SUCCESSFUL_SAVE ||
      pair[1] === Action.CANCEL
    ) {
      values.action = pair[1] as Action;
    } else if (pair[0] === "response") {
      const response = JSON.parse(pair[1]) as TransactionResponse;
      if (!!response && "responseCode" in response) {
        values.action = Action.TRANSACT_RESPONSE;
        values.postTransactionData = response;
      }
    }
    if (pair[0] === "width") {
      values.width = pair[1];
    } else if (pair[0] === "height") {
      values.height = pair[1];
    }
  });
  return values;
};

const defaultHeight = "725px";
const defaultWidth = "750px";

export function PaymentModal({
  modalPayloadState: [modalPayload, setModalPayload],
  onPaymentSuccess,
}: PaymentModalProps) {
  const [paymentAmount, setPaymentAmount] = useState<string>(
    modalPayload?.minimumDeposit && modalPayload?.minimumDeposit > 0
      ? modalPayload?.minimumDeposit.toFixed(2)
      : modalPayload?.remainingBalance && modalPayload?.remainingBalance > 0
      ? modalPayload?.remainingBalance.toFixed(2)
      : "0.00"
  );
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [transactionResponse, setTransactionResponse] =
    useState<TransactionResponse | null>(null);

  const darkModeEnabled = useIsDarkModeEnabled();

  const minimumDepositExists =
    !!modalPayload?.minimumDeposit && modalPayload?.minimumDeposit > 0;

  return (
    <Modal
      className={darkModeEnabled ? undefined : "payment-modal"}
      title={`SO#:${modalPayload?.soNo} - Payment`}
      open={!!modalPayload}
      centered
      footer={null}
      onCancel={() => setModalPayload(null)}
      style={{ paddingBottom: 0 }}
      keyboard={false}
      width={pageNumber === 1 ? 400 : pageNumber === 2 ? 800 : 600}
    >
      {pageNumber === 1 ? (
        <div>
          <Flex
            vertical
            align="center"
            justify="center"
            style={{
              paddingBottom: "0.5rem",
              overflow: "auto",
            }}
            gap={16}
          >
            <Flex vertical gap={12}>
              <Flex align="center" justify="center" gap={10}>
                <Flex vertical>
                  <Typography.Title
                    style={{
                      marginBottom: 0,
                      fontWeight: 500,
                      fontSize: "13px",
                    }}
                    level={5}
                  >
                    Payment Amount
                  </Typography.Title>
                  {minimumDepositExists ? (
                    <Flex vertical>
                      <Radio
                        style={{ fontSize: "16px" }}
                        defaultChecked
                        checked={
                          paymentAmount ===
                          modalPayload?.remainingBalance?.toFixed(2)
                        }
                        value={paymentAmount}
                        onChange={() => {
                          if (modalPayload?.remainingBalance) {
                            setPaymentAmount(
                              modalPayload?.remainingBalance?.toFixed(2)
                            );
                          }
                        }}
                      >
                        {totalDueLabel}: $
                        {modalPayload?.remainingBalance?.toFixed(2)}
                      </Radio>
                      <Radio
                        style={{ fontSize: "16px" }}
                        value={paymentAmount}
                        checked={
                          paymentAmount ===
                          modalPayload?.minimumDeposit?.toFixed(2)
                        }
                        onChange={() => {
                          if (modalPayload?.minimumDeposit) {
                            setPaymentAmount(
                              modalPayload?.minimumDeposit?.toFixed(2)
                            );
                          }
                        }}
                      >
                        Minimum Payment: $
                        {modalPayload?.minimumDeposit?.toFixed(2)}
                      </Radio>
                    </Flex>
                  ) : (
                    <Typography style={{ fontSize: "16px" }}>
                      {totalDueLabel}: $
                      {modalPayload?.remainingBalance?.toFixed(2)}
                    </Typography>
                  )}
                </Flex>
              </Flex>
            </Flex>
            <Button type="primary" onClick={() => setPageNumber(2)}>
              Continue to Payment Details
            </Button>
          </Flex>
        </div>
      ) : pageNumber === 2 ? (
        <AuthorizeAddPaymentAndPayForm
          sono={modalPayload?.soNo ?? ""}
          poNumber={modalPayload?.poNo ?? ""}
          onCancel={() => setModalPayload(null)}
          onPaymentSuccess={(response) => {
            setTransactionResponse(response);
            setPageNumber(3);
            onPaymentSuccess(response);
          }}
          amount={Number(paymentAmount)}
        />
      ) : pageNumber === 3 ? (
        <Flex vertical gap={12} style={{ width: "100%" }}>
          <Typography style={{ margin: "8px 0" }}>
            Thank you for your payment! You will receive a confirmation email
            shortly.
          </Typography>
          <Divider style={{ margin: "0" }} />
          <Typography style={{ fontSize: "17px", margin: "8px 0" }}>
            Payment Total ${Number(transactionResponse?.totalAmount).toFixed(2)}
          </Typography>
          <Flex justify="space-between" gap={12} wrap>
            <Flex vertical gap={4}>
              <Typography.Title level={5} style={{ margin: 0 }}>
                Billing Information
              </Typography.Title>
              <Typography>
                {transactionResponse?.billTo.firstName}{" "}
                {transactionResponse?.billTo.lastName}
              </Typography>
              <Typography>{transactionResponse?.billTo.address}</Typography>
              <Typography>
                {transactionResponse?.billTo.city},{" "}
                {transactionResponse?.billTo.state}{" "}
                {transactionResponse?.billTo.zip}
              </Typography>
              <Typography>{transactionResponse?.billTo.phoneNumber}</Typography>
            </Flex>
            <Flex vertical gap={4}>
              <Typography.Title level={5} style={{ margin: 0 }}>
                Transaction Details
              </Typography.Title>
              <Typography>
                Paid By {transactionResponse?.accountType} ending in{" "}
                {transactionResponse?.accountNumber}
              </Typography>
              <Typography>
                Authorization Code {transactionResponse?.authorization}
              </Typography>
              <Typography>
                Transaction ID {transactionResponse?.transId}
              </Typography>
            </Flex>
          </Flex>
        </Flex>
      ) : null}
    </Modal>
  );
}

function AuthorizeAddPaymentAndPayForm({
  amount,
  onCancel,
  onPaymentSuccess,
  onResizeWindow,
  sono,
  poNumber,
}: {
  amount: number;
  onCancel?: () => void;
  onPaymentSuccess?: (response: TransactionResponse) => void;
  onResizeWindow?: (height: string, width: string) => void;
  sono: string;
  poNumber: string;
}) {
  const [paymentToken, setPaymentToken] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const formRef = useRef<HTMLFormElement>(null);

  const [height, setHeight] = useState<string>(defaultHeight);
  const [width, setWidth] = useState<string>(defaultWidth);

  window.CommunicationHandler = {};
  window.CommunicationHandler.onReceiveCommunication = async (
    queryStr: string
  ) => {
    const param: ActionType = parseResponse(queryStr);
    switch (param?.action ?? "") {
      case Action.RESIZE_WINDOW:
        setHeight(`${param.height ?? defaultHeight}px`);
        setWidth(`${param.width ?? defaultWidth}px`);
        onResizeWindow?.(param.height ?? "", param.width ?? "");
        break;

      case Action.SUCCESSFUL_SAVE:
        break;

      case Action.CANCEL:
        onCancel?.();
        break;
      case Action.TRANSACT_RESPONSE:
        if (param.postTransactionData?.responseCode === ResponseCode.APPROVED) {
          completedLocalTransaction(sono).then(([response, error]) => {
            if (error) {
              console.error("Failed to complete local transaction:", error);
            } else {
              console.log("Local transaction completed successfully");
            }
            if (param.postTransactionData) {
              onPaymentSuccess?.(param.postTransactionData);
            }
          });
        }
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    setIsLoading(true);
    getPaymentToken({
      amount: amount,
      sono: sono,
    }).then(([data, error]) => {
      if (error) {
        return;
      }
      setPaymentToken(data.token);
      setTimeout(() => {
        formRef.current?.submit();
      }, 0);
      setIsLoading(false);
    });
  }, []);

  return (
    <>
      <form
        style={{ display: "hidden" }}
        ref={formRef}
        id="send_token"
        action={`https://accept.authorize.net/payment/payment`}
        method="post"
        target="add_payment"
      >
        <Input type="hidden" name="token" value={paymentToken} />
      </form>
      {isLoading ? (
        <Skeleton
          height={height}
          width={"100%"}
          style={{ borderRadius: "8px", maxWidth: width }}
        />
      ) : (
        <iframe
          title="add_payment"
          id="add_payment"
          name="add_payment"
          frameBorder="0"
          scrolling="no"
          style={{
            height: height,
            maxWidth: width,
            width: "100%",
            borderRadius: "8px",
          }}
        ></iframe>
      )}
    </>
  );
}
